import { Buffer } from 'buffer'
import { LRUMap } from 'lru_map'
import { fcGetDomId } from './fc_dom'
export const FcImageMaxContentLength = 10 * 1024 * 1024
import { FcArea, FcGeneratorOptions } from '@dtx-company/flowcode-generator-types/src'
import { FcFetchData, fcFetchUrl } from './fc_fetch'
import { isNode } from './fc_env'
import { roundDecimal } from './fc_utils'
import axios, { ResponseType } from 'axios'
let apiHost = ''
let cacheForever = false

const mimeTypes: Record<string, string> = {
  '89504E47': 'image/png',
  '47494638': 'image/gif',
  FFD8FFDB: 'image/jpeg',
  FFD8FFE0: 'image/jpeg',
  FFD8FFE1: 'image/jpeg',
  FFD8FFE2: 'image/jpeg',
  FFD8FFE3: 'image/jpeg',
  FFD8FFE8: 'image/jpeg'
}

export interface FcPixelData {
  width: number
  height: number
  pixels: number[][]
}

export interface FcGifInfo {
  isGif: boolean
  delays: number[]
}

interface FcRgbInfo {
  rgb: string
}

const pixelCache = new LRUMap<string, FcPixelData>(256)
const studioConfigCache = new LRUMap<string, FcGeneratorOptions>(256)
const gifInfoCache = new LRUMap<string, FcGifInfo | undefined>(256)
const gifDefaultDelay = 1 / 15
const rgbCache = new LRUMap<string, string>(256)

export async function fcCmykToRgb(cmyk: string): Promise<string> {
  const rgb = rgbCache.get(cmyk)
  if (rgb) {
    return rgb
  }
  const generatorAPIHost = getGeneratorAPIUrl()
  const url = `${generatorAPIHost}/v1/rgb?c=${encodeURIComponent(cmyk)}`
  const response = await fcFetchUrl(url, 'json')
  if (response.status === 200) {
    const rgbInfo = response.data as FcRgbInfo
    rgbCache.set(cmyk, rgbInfo.rgb)
    return rgbInfo.rgb
  } else {
    throw new Error('Could not fetch ' + url + ' ' + response.status)
  }
}

export function fcSetCacheForever(forever: boolean): void {
  cacheForever = forever
}

export function fcGetFrameImageUrl(url: string, frameNumber: number): string {
  const generatorAPIHost = getGeneratorAPIUrl()
  url = encodeURIComponent(url)
  // eslint-disable-next-line @dtx-company/flowcode-rules/no-untranslated-string-literals
  return `${generatorAPIHost}/v1/gif/frame?url=${url}&frameNumber=${frameNumber}`
}

function remapPdfAndEPS(urlStr: string, cmyk: boolean): string {
  if (!urlStr) {
    return urlStr
  }
  // eslint-disable-next-line no-useless-catch
  try {
    const generatorAPIHost = getGeneratorAPIUrl()
    const encUrl = encodeURIComponent(urlStr)
    const url = new URL(urlStr)
    if (url.pathname.toLowerCase().endsWith('.pdf')) {
      // eslint-disable-next-line @dtx-company/flowcode-rules/no-untranslated-string-literals
      return `${generatorAPIHost}/v1/pdf?url=${encUrl}&cmyk=${cmyk}`
    }
    if (url.pathname.toLowerCase().endsWith('.eps')) {
      // eslint-disable-next-line @dtx-company/flowcode-rules/no-untranslated-string-literals
      return `${generatorAPIHost}/v1/eps?url=${encUrl}&cmyk=${cmyk}`
    }
  } catch (e) {
    throw e
  }
  return urlStr
}

export async function fcGetGifInfo(url: string): Promise<FcGifInfo | undefined> {
  const data = gifInfoCache.get(url)
  if (data) {
    return data
  }
  const encUrl = encodeURIComponent(url)
  const generatorAPIHost = getGeneratorAPIUrl()
  // eslint-disable-next-line @dtx-company/flowcode-rules/no-untranslated-string-literals
  url = `${generatorAPIHost}/v1/gif/info?url=${encUrl}`
  const response = await fcFetchUrl(url, 'json')
  if (response.status === 200) {
    const info = response.data as FcGifInfo
    if (!info.isGif || info.delays.length <= 1) {
      gifInfoCache.set(url, undefined)
      return undefined
    }
    if (info.delays) {
      info.delays = info.delays.map(delay => (delay <= 0 ? gifDefaultDelay : delay))
    }
    gifInfoCache.set(url, info)
    return info
  } else {
    throw new Error('Could not fetch ' + url)
  }
}

function getTimeKey(): number | string {
  if (cacheForever) {
    return ''
  }
  return Math.floor(new Date().getTime() / 3600000)
}

export async function fcGetPixelData(
  url: string,
  mask: boolean,
  width: number,
  height: number
): Promise<FcPixelData> {
  if (!url.startsWith('<svg')) {
    url = remapPdfAndEPS(url, false)
  }
  const key = url + width + height + mask + getTimeKey()
  let data = pixelCache.get(key)
  if (data) {
    return data
  }

  const generatorAPIHost = getGeneratorAPIUrl()
  let response: FcFetchData
  if (!isNode() && url.startsWith('blob:')) {
    const blobResponse = await fcFetchUrl(url, 'arraybuffer')
    if (blobResponse.status !== 200) {
      throw new Error('Could not fetch ' + url)
    }
    // eslint-disable-next-line @dtx-company/flowcode-rules/no-untranslated-string-literals
    url = `${generatorAPIHost}/v1/logo?w=${width}&h=${height}`
    if (mask) {
      url = url + '&mask=1'
    }
    response = await imagePostUrl(url, 'arraybuffer', blobResponse.data)
  } else if (url.startsWith('<svg')) {
    const svgData = new TextEncoder().encode(url)
    // eslint-disable-next-line @dtx-company/flowcode-rules/no-untranslated-string-literals
    url = `${generatorAPIHost}/v1/logo?w=${width}&h=${height}`
    if (mask) {
      url = url + '&mask=1'
    }
    response = await imagePostUrl(url, 'arraybuffer', svgData.buffer)
  } else {
    const encUrl = encodeURIComponent(url)
    // eslint-disable-next-line @dtx-company/flowcode-rules/no-untranslated-string-literals
    url = `${generatorAPIHost}/v1/logo?url=${encUrl}&w=${width}&h=${height}`
    if (mask) {
      url = url + '&mask=1'
    }
    response = await fcFetchUrl(url, 'arraybuffer')
  }

  if (response.status === 200) {
    const bufferData = new Uint8Array(Buffer.from(response.data, 'binary'))
    const len = bufferData.length
    let row: number[] = []
    const rows: number[][] = []
    const width = importInt(bufferData, 0)
    const height = importInt(bufferData, 4)
    for (let i = 8; i < len; i++) {
      const byte = bufferData[i]
      for (let j = 0; j < 8; j++) {
        const bit = byte & (1 << j) ? 1 : 0
        row.push(bit)
        if (row.length >= width) {
          rows.push(row)
          row = []
        }
      }
    }
    data = { width, height, pixels: rows }
    pixelCache.set(key, data)
    return data
  } else {
    throw new Error('Could not fetch ' + url)
  }
}

const cache = new LRUMap<string, string>(256)
export async function fcImageToTag(
  cmyk: boolean,
  url: string,
  color?: string,
  isTemplate = false
): Promise<string> {
  url = remapPdfAndEPS(url, cmyk)
  let key = url + (color || '') + getTimeKey()
  if (isTemplate) key += 'template'
  const data = cache.get(key)
  if (data) {
    return data
  }

  const response = await fcFetchUrl(url, 'arraybuffer', FcImageMaxContentLength)
  if (response.status === 200) {
    const responseData = response.data
    let responseType = response.contentType
    if (responseType === 'application/octet-stream') {
      const check = Buffer.from(responseData, 'binary').toString()
      if (check.startsWith('<svg')) {
        responseType = 'image/svg+xml'
      }
    }
    if (responseType === 'image/svg+xml') {
      const svg = stripSVG(Buffer.from(responseData, 'binary').toString(), isTemplate)
      if (color) {
        const svgCol = fcChangeSvgColor(svg, color)
        cache.set(key, svgCol)
        return svgCol
      }
      cache.set(key, svg)
      return svg
    }
    const byteData = Buffer.from(responseData, 'binary')
    const header = byteData.toString('hex', 0, 4).toUpperCase()
    const respTypeFromFile = mimeTypes[header]
    if (respTypeFromFile) {
      responseType = respTypeFromFile
    }
    const imageData = 'data:' + responseType + ';base64,' + byteData.toString('base64')
    if (isTemplate) {
      cache.set(key, imageData)
      return imageData
    } else {
      // eslint-disable-next-line @dtx-company/flowcode-rules/no-untranslated-string-literals
      const img = `<image xlink:href="${imageData}"/>`
      cache.set(key, img)
      return img
    }
  } else {
    throw new Error('Could not fetch ' + url)
  }
}

function stripSVG(image: string, isTemplate: boolean): string {
  image = image.replace(/<\?xml .*\?>\s*/, '')
  image = image.replace(/<desc.*\/desc>\s*/, '')
  image = image.replace(/<title.*\/title>s*/, '')
  image = image.replace(/<!--.*-->\s*/, '')
  if (isTemplate) return image
  const [header] = (image && image.match(/<svg[^>]+>/)) || ['']
  const fixedHeader = header.replace(/ (x|y|width|height|opacity|id)="([^"]+)"/g, '')
  return image.replace(header, fixedHeader)
}

export function fcChangeSvgColor(data: string, color: string): string {
  let svg: string

  const start = 'data:image/svg+xml;base64,'
  if (data.indexOf(start) === 0) {
    svg = Buffer.from(data.substr(start.length), 'base64').toString()
  } else {
    if (data.indexOf('<svg') === -1) {
      return data
    }
    svg = data
  }
  // eslint-disable-next-line @dtx-company/flowcode-rules/no-untranslated-string-literals
  const findColor = new RegExp(`(fill|stroke):([^;"]+)[;"]`, 'gi')
  let match
  let origColor
  while ((match = findColor.exec(svg))) {
    origColor = match[2]
    if (origColor !== 'none') {
      break
    }
    origColor = undefined
  }
  if (!origColor) {
    // eslint-disable-next-line @dtx-company/flowcode-rules/no-untranslated-string-literals
    const findColor2 = new RegExp(` (fill|stroke)="([^"]+)"`, 'gi')
    while ((match = findColor2.exec(svg))) {
      origColor = match[2]
      if (origColor !== 'none') {
        break
      }
      origColor = undefined
    }
  }
  if (origColor) {
    // eslint-disable-next-line @dtx-company/flowcode-rules/no-untranslated-string-literals
    const reFill1 = new RegExp(`(fill|stroke)="(${origColor})"`, 'g')
    // eslint-disable-next-line @dtx-company/flowcode-rules/no-untranslated-string-literals
    const reFill2 = new RegExp(`(fill|stroke):${origColor}`, 'g')
    // eslint-disable-next-line @dtx-company/flowcode-rules/no-untranslated-string-literals
    svg = svg.replace(reFill1, `$1="${color}"`)
    // eslint-disable-next-line @dtx-company/flowcode-rules/no-untranslated-string-literals
    svg = svg.replace(reFill2, `$1:${color}`)
  } else {
    // eslint-disable-next-line @dtx-company/flowcode-rules/no-untranslated-string-literals
    svg = svg.replace(/<(path|circle|rect|ellipse|line|polyline) /g, `<$1 fill="${color}" `)
  }
  return svg
}

export function fcGetSvgDimensions(svg: string): FcArea | undefined {
  const matchStr = /<svg .*?viewBox=["'](-?[\d.]+[, ]+-?[\d.]+[, ])([\d.]+[, ])([\d.]+)["']/
  const dims = svg.match(matchStr)
  if (dims && dims.length > 3) {
    return new FcArea(0, 0, parseFloat(dims[2]), parseFloat(dims[3]))
  }
  return undefined
}

export function fcImageAddSize(
  imgTag: string,
  x: number,
  y: number,
  width: number,
  height: number,
  id: string,
  opacity?: number,
  rotate?: number,
  maskId?: string
): string {
  if (!id) {
    id = 'id-' + fcGetDomId()
  }
  let rotateId = ''
  if (rotate) {
    rotateId = id
    id = id + '-i'
  }

  const isSvg = imgTag.startsWith('<svg')
  if (isSvg) {
    // make class names unique so multiple svgs with same classnames work ok
    const classMatch = /([.,a-zA-z0-9-_]{1,100})\s*\{/g
    let cls
    const allClassnames: string[] = []
    const allClassnamesSet: Map<string, boolean> = new Map<string, boolean>()
    while ((cls = classMatch.exec(imgTag)) !== null) {
      const classNames = cls[1].trim().split(',')
      classNames.forEach(className => {
        if (className.startsWith('.')) {
          className = className.substr(1)
        }
        if (!allClassnamesSet.get(className)) {
          allClassnamesSet.set(className, true)
          allClassnames.push(className)
        }
      })
    }
    allClassnames.forEach(className => {
      const newClassName = `${id}-${className}`
      const re1 = new RegExp(`(['"])(${className})(['"])`, 'g')
      imgTag = imgTag.replace(re1, `$1${newClassName}$3`)
      const re2 = new RegExp(`(\\.${className})([^a-zA-Z0-9-_])`, 'g')
      imgTag = imgTag.replace(re2, `.${newClassName}$2`)
    })
    const allIds: string[] = []
    const allIdsSet: Map<string, boolean> = new Map<string, boolean>()
    const idMatch = /(id\s*=\s*)(['"])([a-zA-z0-9-_]+)(['"])/g
    let idPat
    while ((idPat = idMatch.exec(imgTag)) !== null) {
      const idName = idPat[3].trim()
      if (!allIdsSet.get(idName) && !idName.startsWith(id)) {
        allIdsSet.set(idName, true)
        allIds.push(idName)
      }
    }
    allIds.forEach(idName => {
      const newIdName = `${id}-${idName}`
      const re1 = new RegExp(`(['"])(${idName})(['"])`, 'g')
      imgTag = imgTag.replace(re1, `$1${newIdName}$3`)
      const re2 = new RegExp(`(#${idName})([^a-zA-Z0-9-_])`, 'g')
      imgTag = imgTag.replace(re2, `#${newIdName}$2`)
    })
  }
  const tag = isSvg ? '<svg' : '<image'
  const maskStr = maskId ? ` mask="url(#${maskId})"` : ''
  const resultTag = imgTag.replace(
    tag,
    `${tag}${maskStr} x="${roundDecimal(x)}" y="${roundDecimal(y)}" width="${roundDecimal(
      width
    )}" height="${roundDecimal(height)}" id="${id}" opacity="${opacity || 1.0}"`
  )
  if (rotate) {
    return `<g id="${rotateId}" transform="rotate(${rotate} ${width / 2} ${
      height / 2
    })">${resultTag}</g>`
  } else {
    return resultTag
  }
}

export async function fcGetPixelDataRaw(
  url: string,
  width: number,
  height: number
): Promise<FcPixelData> {
  if (!url.startsWith('<svg')) {
    url = remapPdfAndEPS(url, false)
  }

  const key = 'raw:' + url + width + height + getTimeKey()
  let data = pixelCache.get(key)
  if (data) {
    return data
  }

  const generatorAPIHost = getGeneratorAPIUrl()
  let response: FcFetchData
  if (!isNode() && url.startsWith('blob:')) {
    const blobResponse = await fcFetchUrl(url, 'arraybuffer')
    if (blobResponse.status !== 200) {
      throw new Error('Could not fetch ' + url)
    }
    url = `${generatorAPIHost}/v1/logoimg?w=${width}&h=${height}`
    response = await imagePostUrl(url, 'arraybuffer', blobResponse.data)
  } else if (url.startsWith('<svg')) {
    const svgData = new TextEncoder().encode(url)
    url = `${generatorAPIHost}/v1/logoimg?w=${width}&h=${height}`
    response = await imagePostUrl(url, 'arraybuffer', svgData.buffer)
  } else {
    const encUrl = encodeURIComponent(url)
    url = `${generatorAPIHost}/v1/logoimg?url=${encUrl}&w=${width}&h=${height}`
    response = await fcFetchUrl(url, 'arraybuffer')
  }
  if (response.status === 200) {
    const bufferData = new Uint8Array(Buffer.from(response.data, 'binary'))
    const len = bufferData.length
    let row: number[] = []
    const rows: number[][] = []
    const width = importInt(bufferData, 0)
    const height = importInt(bufferData, 4)
    for (let i = 8; i < len; i += 3) {
      const r = bufferData[i]
      const g = bufferData[i + 1]
      const b = bufferData[i + 2]
      const val = (r << 16) | (g << 8) | b
      row.push(val)
      if (row.length >= width) {
        rows.push(row)
        row = []
      }
    }
    data = { width, height, pixels: rows }
    pixelCache.set(key, data)
    return data
  } else {
    throw new Error('Could not fetch ' + url)
  }
}

function getEnvironment(): string {
  let environment = ''
  if (isNode()) {
    environment = process.env.NODE_ENV || 'production'
  } else {
    const loc = window.location?.href || ''
    if (loc.startsWith('https://stg')) {
      environment = 'staging'
    } else if (loc.startsWith('https://preprod')) {
      environment = 'preprod'
    } else {
      environment = 'production'
    }
  }
  return environment
}

function getApiUrl(): string {
  if (apiHost !== '') {
    return apiHost
  }
  const environment = getEnvironment()
  switch (environment) {
    case 'preprod':
      apiHost = 'https://api.preprod.flowcode.com/'
      break
    case 'staging':
      apiHost = 'https://api.stg.flowcode.com/'
      break
    default:
      apiHost = 'https://api.flowcode.com/'
      break
  }
  return apiHost
}

export function getGeneratorAPIUrl(): string {
  let generatorAPIHost = ''
  const environment = getEnvironment()
  switch (environment) {
    case 'development':
      generatorAPIHost = 'http://localhost:3000'
      break
    case 'preprod':
      generatorAPIHost = 'https://preprod-generator.flowcode.com'
      break
    case 'staging':
      generatorAPIHost = 'https://stg-generator.flowcode.com'
      break
    default:
      generatorAPIHost = 'https://generator.flowcode.com'
      break
  }
  return generatorAPIHost
}

export async function fcGetStudioConfig(id: string): Promise<FcGeneratorOptions | undefined> {
  const key = id + getTimeKey()
  const data = studioConfigCache.get(key)
  if (data) {
    return data
  }
  // eslint-disable-next-line no-useless-catch
  try {
    const apiUrl = getApiUrl()
    const url = `${apiUrl}v2/flowcode/studio/config/${id}`
    const response = await fcFetchUrl(url, 'json')
    if (response.status === 200) {
      const options = response.data.config ? JSON.parse(response.data.config) : {}
      studioConfigCache.set(key, options)
      return options
    } else {
      throw new Error('Could not fetch ' + url)
    }
  } catch (err) {
    throw err
  }
}

function importInt(rowBuffer: Uint8Array, ind: number): number {
  let val = 0
  val |= rowBuffer[ind] << 24
  val |= rowBuffer[ind + 1] << 16
  val |= rowBuffer[ind + 2] << 8
  val |= rowBuffer[ind + 3]
  return val
}

async function imagePostUrl(
  url: string,
  responseType: ResponseType,
  data: ArrayBuffer,
  maxLength?: number
): Promise<FcFetchData> {
  if (!maxLength) {
    maxLength = FcImageMaxContentLength
  }
  try {
    const response = await axios.post(url, data, {
      responseType,
      maxContentLength: maxLength,
      headers: { 'Content-Type': 'application/octet-stream' }
    })
    if (response.status !== 200) {
      return { status: response.status }
    }
    const contentType = response.headers['content-type']
    return { data: response.data, contentType, status: 200 }
  } catch (err) {
    return { status: 500, error: err.toString() }
  }
}
